import { DatePipe, LocationStrategy, PathLocationStrategy, registerLocaleData, ViewportScroller } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeFr from '@angular/common/locales/fr';
import { LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, Scroll } from '@angular/router';
import {
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MSAL_INTERCEPTOR_CONFIG,
	MsalGuard,
	MsalGuardConfiguration,
	MsalInterceptor,
	MsalInterceptorConfiguration,
	MsalModule,
	MsalRedirectComponent,
} from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIconsModule } from '@ng-icons/core';
import { simpleAdobeacrobatreader, simpleMicrosoftexcel } from '@ng-icons/simple-icons';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { SharedModule } from '@shared/shared.module';
import { ToastrModule } from 'ngx-toastr';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AccessInterceptor } from './core/interceptors/access-interceptor';
registerLocaleData(localeFr);

export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: {
			clientId: environment.clientId,
			authority: environment.authority,
			redirectUri: `${environment.redirectUri}/auth`,
			postLogoutRedirectUri: environment.redirectUri,
			navigateToLoginRequestUrl: true,
		},
		cache: {
			cacheLocation: BrowserCacheLocation.LocalStorage,
			storeAuthStateInCookie: false,
		},
		system: {
			loggerOptions: {
				logLevel: LogLevel.Info,
				piiLoggingEnabled: false,
			},
			tokenRenewalOffsetSeconds: 300,
		},
	});
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap: new Map([[environment.backEndApiUrl, [`${environment.audience}${environment.scope}`]]]),
	};
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: {
			scopes: [`${environment.audience}${environment.scope}`],
		},
	};
}

@NgModule({
	declarations: [AppComponent],
	bootstrap: [AppComponent, MsalRedirectComponent],
	imports: [
		BrowserModule,
		SharedModule,
		AppRoutingModule,
		ToastrModule.forRoot({
			positionClass: 'toast-top-right',
			maxOpened: 3,
			autoDismiss: true,
		}),
		NgIconsModule.withIcons({ simpleMicrosoftexcel, simpleAdobeacrobatreader }),
		TranslateModule.forRoot(),
		BrowserAnimationsModule,
		MsalModule.forRoot(MSALInstanceFactory(), MSALGuardConfigFactory(), MSALInterceptorConfigFactory()),
		NgbModule,
	],
	providers: [
		{ provide: LOCALE_ID, useValue: 'fr-FR' },
		{ provide: LocationStrategy, useClass: PathLocationStrategy },
		{ provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
		{ provide: HTTP_INTERCEPTORS, useClass: AccessInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
		{ provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
		{ provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
		MsalGuard,
		DatePipe,
		provideHttpClient(withInterceptorsFromDi()),
		...provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
	],
})
export class AppModule {
	constructor(router: Router, viewportScroller: ViewportScroller) {
		viewportScroller.setOffset([0, 250]);
		router.events.pipe(filter((e) => e instanceof Scroll)).subscribe((event) => {
			const e = event as Scroll;
			if (e.anchor) {
				// anchor navigation
				setTimeout(() => {
					viewportScroller.scrollToAnchor(e.anchor!);
				});
			} else if (e.position) {
				// backward navigation
				viewportScroller.scrollToPosition(e.position);
			} else {
				// forward navigation
				viewportScroller.scrollToPosition([0, 0]);
			}
		});
	}
}
